import React, { useState } from 'react';
import { Button } from 'components/Button';

type EvidenceMediaProps = { url: string; index: number; video?: boolean };

const EvidenceMedia = ({ url, index, video }: EvidenceMediaProps) => {
  const [failed, setFailed] = useState(false);
  let safeUrl: string | null = null;
  try {
    const parsed = new URL(url);
    if (['https:', 'http:'].includes(parsed.protocol)) safeUrl = parsed.href;
  } catch {
    // Incomplete media references stay visible as unavailable evidence.
  }
  const label = `Customer evidence ${video ? 'video' : 'photo'} ${index + 1}`;

  return (
    <div className="rounded border border-gray-200 bg-gray-50 p-3">
      {!safeUrl || failed ? (
        <p role="alert" className="text-sm text-amber-800">{label} could not be loaded.</p>
      ) : video ? (
        <video
          aria-label={label}
          src={safeUrl}
          controls
          preload="metadata"
          className="max-h-72 w-full"
          onError={() => setFailed(true)}
        />
      ) : (
        <img
          src={safeUrl}
          alt={label}
          className="h-48 w-full object-contain"
          onError={() => setFailed(true)}
        />
      )}
      {safeUrl && (
        <a href={safeUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-sm font-medium text-blue-700">
          Open {video ? 'video' : 'photo'} {index + 1}
        </a>
      )}
    </div>
  );
};

type DefectReviewPanelProps = {
  status: string;
  closed: boolean;
  images?: string[] | null;
  photosRequired?: boolean;
  videos?: string[] | null;
  acknowledged: boolean;
  detailsReady: boolean;
  canDecide: boolean;
  approvalUnavailable?: boolean;
  updating: boolean;
  onAcknowledge: (checked: boolean) => void;
  onApprove: () => void;
  onDeny: () => void;
  onReload: () => void;
};

export const DefectReviewPanel = ({
  status, closed, images, photosRequired = true, videos, acknowledged, detailsReady, canDecide, updating, approvalUnavailable,
  onAcknowledge, onApprove, onDeny, onReload,
}: DefectReviewPanelProps) => (
  <section aria-labelledby="defect-evidence-title" className="mb-8 overflow-hidden rounded-lg border border-amber-200 bg-white">
    <div className="border-b border-amber-200 bg-amber-50 px-6 py-4">
      <h2 id="defect-evidence-title" className="font-semibold text-gray-900">Defect evidence review</h2>
      <p className="mt-1 text-sm text-gray-700">
        {status === 'DENIED'
          ? 'This defect request was denied. The customer can view the denial explanation.'
          : closed
          ? 'This defect request is closed. The evidence and review remain available here.'
          : status === 'APPROVED'
          ? 'Evidence approved. Choose and issue the appropriate resolution.'
          : status === 'MORE_INFORMATION_REQUESTED'
          ? 'Waiting for the customer to answer your information request. Evidence review resumes after their response.'
          : 'Review the customer notes, item comments, and evidence before approving or denying this request. Resolution options remain unavailable to the customer during review.'}
      </p>
    </div>
    <div className="space-y-4 p-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {(images || []).map((url, index) => <EvidenceMedia key={`photo-${index}-${url}`} url={url} index={index} />)}
        {(videos || []).map((url, index) => <EvidenceMedia key={`video-${index}-${url}`} url={url} index={index} video />)}
      </div>
      {photosRequired && !images?.length && <p className="text-sm text-amber-800">At least one customer photo is required before approval. Videos are optional supporting evidence.</p>}
      {!detailsReady ? (
        <div role="alert" className="text-sm text-amber-800">
          <p>Review details are incomplete. Reload this request before making a decision.</p>
          <Button variant="outlined" onClick={onReload} className="mt-2">Reload request</Button>
        </div>
      ) : canDecide && (
        <>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
            <input type="checkbox" checked={acknowledged} disabled={updating} onChange={(event) => onAcknowledge(event.target.checked)} />
            I have reviewed the submitted evidence
          </label>
          <p className="text-sm text-gray-600">Approval records your review. Choose a resolution separately after approval.</p>
          <div className="flex flex-wrap gap-3">
            {!approvalUnavailable && <Button onClick={onApprove} disabled={!acknowledged || updating || (photosRequired && !images?.length)} isLoading={updating}>Approve defect request</Button>}
            <Button onClick={onDeny} disabled={!acknowledged || updating} variant="outlined" color="danger">Deny defect request</Button>
          </div>
        </>
      )}
    </div>
  </section>
);
